/**
 * Blog API — request contracts.
 *
 *   GET    /v1/blog/posts                       (no auth)
 *   GET    /v1/blog/posts/:slug                 (no auth)
 *   GET    /v1/admin/blog/posts                 (super-admin)
 *   POST   /v1/admin/blog/posts                 (super-admin)
 *   GET    /v1/admin/blog/posts/:postId         (super-admin)
 *   PATCH  /v1/admin/blog/posts/:postId/draft   (super-admin)
 *   POST   /v1/admin/blog/posts/:postId/publish (super-admin)
 *   POST   /v1/admin/blog/posts/:postId/unpublish (super-admin)
 *   DELETE /v1/admin/blog/posts/:postId/draft   (super-admin; discard changes to a published post)
 *   POST   /v1/admin/blog/assets/upload-url     (super-admin)
 *   GET    /v1/blog/categories                  (no auth; categories with a published post)
 *   GET    /v1/admin/blog/categories            (super-admin; every category + postCount)
 *   POST   /v1/admin/blog/categories            (super-admin)
 *   PATCH  /v1/admin/blog/categories/:categoryId (super-admin; name only)
 *   PUT    /v1/admin/blog/categories/order      (super-admin)
 *   DELETE /v1/admin/blog/categories/:categoryId (super-admin; refused while in use)
 *
 * The blog is platform-global — no route carries a school public id.
 */

import { z } from 'zod'

import { BlogCategoryNameSchema } from '../../schemas/entities/blog-category'
import { BlogPostContentSchema, BlogPostVersionStatusSchema, HttpsUrlSchema } from '../../schemas/entities/blog-post'

const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/

export const BlogPostSlugSchema = z
  .string()
  .min(1)
  .max(64)
  .regex(SLUG_PATTERN, 'Slug must be lowercase ASCII letters, digits, and hyphens')

const BlogPostIdParam = z.object({ postId: z.string().min(1) })

// ============================================
// Public reads (no auth)
// ============================================

export const ListPublicBlogPostsQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(100).optional(),
  /** A category slug. An unknown slug yields an empty list; the category page decides 404 from the categories read. */
  category: BlogPostSlugSchema.optional()
})
export type ListPublicBlogPostsQuery = z.infer<typeof ListPublicBlogPostsQuerySchema>

export const GetPublicBlogPostBySlugParamsSchema = z.object({ slug: BlogPostSlugSchema })
export type GetPublicBlogPostBySlugParams = z.infer<typeof GetPublicBlogPostBySlugParamsSchema>

// ============================================
// Admin reads
// ============================================

export const ListBlogPostsQuerySchema = z.object({
  /** Omitted = every post regardless of status. */
  status: BlogPostVersionStatusSchema.optional()
})
export type ListBlogPostsQuery = z.infer<typeof ListBlogPostsQuerySchema>

export const GetBlogPostParamsSchema = BlogPostIdParam
export type GetBlogPostParams = z.infer<typeof GetBlogPostParamsSchema>

// ============================================
// Admin writes
// ============================================

export const CreateBlogPostRequestSchema = z
  .object({
    title: z.string().min(1).max(255),
    slug: BlogPostSlugSchema,
    excerpt: z.string().max(500).optional(),
    heroImageUrl: HttpsUrlSchema.optional(),
    categoryPublicId: z.string().min(1).optional()
  })
  .strict()
export type CreateBlogPostRequest = z.infer<typeof CreateBlogPostRequestSchema>

export const UpdateBlogPostDraftParamsSchema = BlogPostIdParam
export type UpdateBlogPostDraftParams = z.infer<typeof UpdateBlogPostDraftParamsSchema>

export const UpdateBlogPostDraftRequestSchema = z
  .object({
    title: z.string().min(1).max(255).optional(),
    // Editable at any time, including after publish (SCH-2170). Renaming a
    // published post changes its live URL; the old /blog/<slug> 404s, with no
    // redirect. Still globally unique: a collision is 409 BLOG_SLUG_TAKEN.
    slug: BlogPostSlugSchema.optional(),
    excerpt: z.string().max(500).nullable().optional(),
    heroImageUrl: HttpsUrlSchema.nullable().optional(),
    // Post metadata, like `excerpt`: null clears the category, omitted leaves
    // it alone. Live on the public site without a re-publish.
    categoryPublicId: z.string().min(1).nullable().optional(),
    // Omitted for a metadata-only edit (e.g. inline rename); the backend only
    // writes a draft version when real content is present.
    content: BlogPostContentSchema.optional()
  })
  .strict()
export type UpdateBlogPostDraftRequest = z.infer<typeof UpdateBlogPostDraftRequestSchema>

export const PublishBlogPostParamsSchema = BlogPostIdParam
export type PublishBlogPostParams = z.infer<typeof PublishBlogPostParamsSchema>

export const PublishBlogPostRequestSchema = z
  .object({
    draftPublicId: z.string().min(1),
    /**
     * HTML rendered from the draft's blocks with `blocksToFullHTML`. Produced by
     * the web tier (a Next server action, mirroring the email-bake action), not
     * by this API — the backend has no React. Optional: a caller that cannot
     * render still publishes, and the version row simply stores no HTML cache.
     * NEVER trusted as received — the backend sanitizes before storing, and
     * block JSON stays the source of truth.
     */
    renderedHtml: z.string().max(2_000_000).optional(),
    /**
     * Identifies the renderer that produced `renderedHtml`, so staleness is
     * queryable after a BlockNote upgrade or a block-markup change. Only the
     * renderer knows this, so the backend stores it verbatim rather than
     * stamping its own constant — web and backend deploy separately, so a
     * backend-owned value would lie under skew. Bounded, but not semantically
     * validated: it is metadata about the cache, never content.
     */
    rendererVersion: z.string().max(64).optional()
  })
  .strict()
  .refine(value => value.rendererVersion === undefined || value.renderedHtml !== undefined, {
    message: 'rendererVersion requires renderedHtml',
    path: ['rendererVersion']
  })
export type PublishBlogPostRequest = z.infer<typeof PublishBlogPostRequestSchema>

export const UnpublishBlogPostParamsSchema = BlogPostIdParam
export type UnpublishBlogPostParams = z.infer<typeof UnpublishBlogPostParamsSchema>

export const DiscardBlogPostDraftParamsSchema = BlogPostIdParam
export type DiscardBlogPostDraftParams = z.infer<typeof DiscardBlogPostDraftParamsSchema>

export const BlogAssetsUploadUrlRequestSchema = z
  .object({
    fileName: z.string().min(1).max(255),
    contentType: z.string().min(1).max(255)
  })
  .strict()
export type BlogAssetsUploadUrlRequest = z.infer<typeof BlogAssetsUploadUrlRequestSchema>

// ============================================
// Categories (SCH-2108)
// ============================================

const BlogCategoryIdParam = z.object({ categoryId: z.string().min(1) })

export const CreateBlogCategoryRequestSchema = z
  .object({
    name: BlogCategoryNameSchema,
    // Fixed at creation: the update route accepts `name` only, so a live
    // /blog/category/<slug> URL cannot break.
    slug: BlogPostSlugSchema
  })
  .strict()
export type CreateBlogCategoryRequest = z.infer<typeof CreateBlogCategoryRequestSchema>

export const UpdateBlogCategoryParamsSchema = BlogCategoryIdParam
export type UpdateBlogCategoryParams = z.infer<typeof UpdateBlogCategoryParamsSchema>

/** Strict: a body that also carries `slug` is rejected, never silently ignored. */
export const UpdateBlogCategoryRequestSchema = z.object({ name: BlogCategoryNameSchema }).strict()
export type UpdateBlogCategoryRequest = z.infer<typeof UpdateBlogCategoryRequestSchema>

export const DeleteBlogCategoryParamsSchema = BlogCategoryIdParam
export type DeleteBlogCategoryParams = z.infer<typeof DeleteBlogCategoryParamsSchema>

/** Upper bound on one reorder request; far above any real category list. */
const REORDER_BLOG_CATEGORIES_MAX = 200

/**
 * Every category's public id, in the new order. Mirrors the home-card reorder
 * contract (`api/home/reorder.ts`): positions never cross the wire, the server
 * renumbers densely from 1 and rejects an id set that differs from the stored one.
 */
export const ReorderBlogCategoriesRequestSchema = z
  .object({
    publicIds: z
      .array(z.string().min(1))
      .min(1)
      .max(REORDER_BLOG_CATEGORIES_MAX)
      .refine(ids => new Set(ids).size === ids.length, { message: 'Duplicate category ids' })
  })
  .strict()
export type ReorderBlogCategoriesRequest = z.infer<typeof ReorderBlogCategoriesRequestSchema>
